import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Column, DataRow, FormField, InputTextField, Row, Typography } from '@tempmee/react';
import { LabelledSelectField } from '../../../../commonComponents/LabelledSelectField';
import { STATUS_CONFIG } from './credentialV2Utils';

/** Inline validation message; the form's save scrolls to the first `data-field-error`. */
export const FieldError = ({ message }) => {
  if (!message) return null;
  return (
    <Typography variant="caption" className="text-error-500" data-field-error="true">
      {message}
    </Typography>
  );
};

FieldError.propTypes = {
  message: PropTypes.string,
};

FieldError.defaultProps = {
  message: null,
};

const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Width of a credential card's field column, shared so every row lines up. Matches the
 * name-change section; at 50% a MacBook-width card wrapped the longer labels.
 */
export const CREDENTIAL_FIELD_WIDTH = '75%';

/** A read-only label/value row of a credential card: the DS DataRow, at the card's field width. */
export const CredentialInfoRow = ({ label, value }) => (
  <Box width={CREDENTIAL_FIELD_WIDTH}>
    <DataRow label={label} value={value} className="px-3" />
  </Box>
);

CredentialInfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

// Edit-mode counterpart of CredentialInfoRow: DataRow's label tone, with a field in place of the
// value (DataRow wraps its value in a paragraph, which cannot hold an input).
const CredentialFieldRow = ({ label, error, children }) => (
  <Row
    justify="between"
    align="center"
    gap={2}
    px={3}
    py={1}
    width={CREDENTIAL_FIELD_WIDTH}
    className={`border-b border-solid ${error ? 'border-error-500' : 'border-divider'}`}
  >
    <Typography variant="body2" className="text-text-secondary">
      {label}
    </Typography>
    {children}
  </Row>
);

CredentialFieldRow.propTypes = {
  label: PropTypes.string.isRequired,
  /** Turns the divider into the error color. */
  error: PropTypes.string,
  children: PropTypes.node.isRequired,
};

CredentialFieldRow.defaultProps = {
  error: null,
};

/**
 * One label/date row of a credential card. In edit mode the input has a fixed width, so a
 * row's date picker and a disabled "No expiration" box beside it line up (GOTU-19450).
 */
export const CredentialDateRow = ({ label, value, editMode, error, disabled, onChange }) => {
  if (!editMode) {
    return (
      <CredentialInfoRow
        label={label}
        value={value ? moment.utc(value).format(DATE_FORMAT) : '-'}
      />
    );
  }

  return (
    <CredentialFieldRow label={label} error={error}>
      <Column gap={1}>
        <InputTextField
          aria-label={label}
          className="w-40"
          // The inner <input> is flex-1 without min-width: 0, so a text input keeps its native
          // 20-char width and overflows the w-40 box (and the table scrolls). size=1 lets it fit.
          size={1}
          type={disabled ? 'text' : 'date'}
          disabled={disabled}
          isError={!!error}
          value={(() => {
            if (disabled) return '-';
            // UTC, like view mode and the save: a stored midnight-UTC date must not show as the
            // previous day in the picker for anyone west of UTC.
            return value ? moment.utc(value).format(DATE_FORMAT) : '';
          })()}
          onChange={(e) => onChange(e.target.value)}
        />
        {!disabled && <FieldError message={error} />}
      </Column>
    </CredentialFieldRow>
  );
};

CredentialDateRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  editMode: PropTypes.bool.isRequired,
  error: PropTypes.string,
  /** Shows a disabled "-" box instead of the date picker ("No expiration date"). */
  disabled: PropTypes.bool,
  /** Receives the new value (`YYYY-MM-DD`, or '' when cleared). */
  onChange: PropTypes.func.isRequired,
};

CredentialDateRow.defaultProps = {
  value: null,
  error: null,
  disabled: false,
};

/** A labelled text value of a credential card: an input in edit mode, a data row otherwise. */
export const CredentialTextField = ({
  id,
  label,
  value,
  editMode,
  error,
  onChange,
  inputMode,
  maxLength,
}) => {
  if (!editMode) {
    // px-3 lines the label up with the date rows (CredentialInfoRow) below it.
    return <DataRow label={label} value={value || '-'} orientation="stacked" className="px-3" />;
  }

  return (
    <FormField label={label} htmlFor={id} labelWeight="normal" className="px-3">
      <Column gap={1}>
        <InputTextField
          id={id}
          value={value || ''}
          isError={!!error}
          inputMode={inputMode}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)}
        />
        <FieldError message={error} />
      </Column>
    </FormField>
  );
};

CredentialTextField.propTypes = {
  /** Links the label to the input; must be unique on the page. */
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  editMode: PropTypes.bool.isRequired,
  error: PropTypes.string,
  /** Receives the new text. */
  onChange: PropTypes.func.isRequired,
  inputMode: PropTypes.string,
  maxLength: PropTypes.number,
};

CredentialTextField.defaultProps = {
  value: '',
  error: null,
  inputMode: undefined,
  maxLength: undefined,
};

// The statuses an admin can set. `pending` is a deprecated backend alias and is not offered.
export const CREDENTIAL_STATUS_OPTIONS = [
  'pending_verification',
  'verified',
  'invalid',
  'expired',
].map((status) => ({ value: status, label: STATUS_CONFIG[status].label }));

export const CredentialStatusSelect = ({ value, onChange }) => (
  <LabelledSelectField
    ariaLabel="License status"
    className="w-48"
    options={CREDENTIAL_STATUS_OPTIONS}
    value={value || undefined}
    placeholder="Select status"
    onChange={onChange}
  />
);

CredentialStatusSelect.propTypes = {
  value: PropTypes.string,
  /** Receives the chosen status value. */
  onChange: PropTypes.func.isRequired,
};

CredentialStatusSelect.defaultProps = {
  value: '',
};
